import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, RefreshCw, ClipboardCheck, Trash2 } from 'lucide-react';

const STATUS_BADGE = {
  active:              'bg-emerald-100 text-emerald-800',
  under_maintenance:   'bg-amber-100 text-amber-800',
  disposed:            'bg-slate-100 text-slate-500 line-through',
};

const CATEGORIES = [
  'Furniture', 'Air Conditioning', 'Electrical', 'Vehicle',
  'Generator / UPS', 'Printer / Copier', 'Projector / AV',
  'Security Equipment', 'Pantry Appliance', 'Other',
];

const EMPTY_ASSET = {
  name: '', category: 'Furniture', serial_no: '', location: '',
  purchase_date: '', purchase_value: '', vendor_name: '',
  warranty_expiry: '', assigned_to_department: '', notes: '',
};

export default function AdminAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ASSET);
  const [editId, setEditId] = useState(null);
  const [auditTarget, setAuditTarget] = useState(null);
  const [auditForm, setAuditForm] = useState({ audited_by: '', condition: 'good', location_confirmed: true, remarks: '', next_audit_due: '' });
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [auditDue, setAuditDue] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterCat) q.set('category', filterCat);
      if (filterStatus) q.set('status', filterStatus);
      const [d, ad] = await Promise.all([
        apiGet(`/admin/assets?${q}`),
        apiGet('/admin/assets/audit-due'),
      ]);
      setAssets(d.assets || []);
      setAuditDue(ad.assets || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterCat, filterStatus]); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, purchase_value: form.purchase_value ? parseFloat(form.purchase_value) : null };
      if (editId) {
        await apiPut(`/admin/assets/${editId}`, body);
        toast.success('Asset updated');
      } else {
        await apiPost('/admin/assets', body);
        toast.success('Asset created');
      }
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_ASSET);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const handleAudit = async (e) => {
    e.preventDefault();
    try {
      await apiPost(`/admin/assets/${auditTarget}/audit`, auditForm);
      toast.success('Audit recorded');
      setAuditTarget(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Audit failed'); }
  };

  const openEdit = (a) => {
    setForm({
      name: a.name || '', category: a.category || 'Furniture',
      serial_no: a.serial_no || '', location: a.location || '',
      purchase_date: a.purchase_date || '', purchase_value: a.purchase_value || '',
      vendor_name: a.vendor_name || '', warranty_expiry: a.warranty_expiry || '',
      assigned_to_department: a.assigned_to_department || '', notes: a.notes || '',
    });
    setEditId(a.id);
    setShowForm(true);
  };

  return (
    <div data-testid="admin-assets" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Admin · SOP I1–I4</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Admin Asset Register</h1>
          <p className="text-sm text-slate-500 mt-1">Furniture · ACs · Vehicles · Generators · AV Equipment</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={12} />
          </button>
          <button
            onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_ASSET); }}
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856]"
            data-testid="add-asset-btn"
          >
            <Plus size={12} /> Add Asset
          </button>
        </div>
      </div>

      {/* Audit due banner */}
      {auditDue.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center gap-2 text-sm text-amber-800" data-testid="audit-due-banner">
          <ClipboardCheck size={14} className="text-amber-600 shrink-0" />
          <span><strong>{auditDue.length}</strong> asset{auditDue.length > 1 ? 's' : ''} overdue for physical verification</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="border border-slate-200 rounded-sm px-2.5 py-1.5 text-xs">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-200 rounded-sm px-2.5 py-1.5 text-xs">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="under_maintenance">Under Maintenance</option>
          <option value="disposed">Disposed</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No assets found</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Tag', 'Name', 'Category', 'Location', 'Dept', 'Purchase Value', 'Warranty', 'Last Audited', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {assets.map(a => (
                <tr key={a.id} className={`hover:bg-slate-50 ${a.status === 'disposed' ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{a.asset_tag}</td>
                  <td className="px-3 py-2 font-display font-semibold">{a.name}</td>
                  <td className="px-3 py-2 text-slate-500">{a.category}</td>
                  <td className="px-3 py-2">{a.location || '—'}</td>
                  <td className="px-3 py-2">{a.assigned_to_department || '—'}</td>
                  <td className="px-3 py-2">{a.purchase_value ? `₹${Number(a.purchase_value).toLocaleString('en-IN')}` : '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{a.warranty_expiry || '—'}</td>
                  <td className={`px-3 py-2 whitespace-nowrap ${!a.last_audited_at ? 'text-red-500 font-semibold' : ''}`}>
                    {a.last_audited_at ? a.last_audited_at.slice(0, 10) : 'Never'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${STATUS_BADGE[a.status] || ''}`}>
                      {a.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      {a.status !== 'disposed' && (
                        <>
                          <button onClick={() => openEdit(a)} className="text-[#3b4872] hover:underline text-[10px] font-semibold" data-testid={`edit-asset-${a.id}`}>Edit</button>
                          <button
                            onClick={() => { setAuditTarget(a.id); setAuditForm({ audited_by: '', condition: 'good', location_confirmed: true, remarks: '', next_audit_due: '' }); }}
                            className="text-emerald-700 hover:underline text-[10px] font-semibold"
                            data-testid={`audit-asset-${a.id}`}
                          >
                            Audit
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Asset form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display font-bold text-lg">{editId ? 'Edit Asset' : 'Add New Asset'}</h2>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid="asset-name-input" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Category *</label>
                  <select required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Serial No.</label>
                  <input value={form.serial_no} onChange={e => setForm(f => ({ ...f, serial_no: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Location</label>
                  <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Department</label>
                  <input value={form.assigned_to_department} onChange={e => setForm(f => ({ ...f, assigned_to_department: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Purchase Date</label>
                  <input type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Purchase Value (₹)</label>
                  <input type="number" value={form.purchase_value} onChange={e => setForm(f => ({ ...f, purchase_value: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Vendor</label>
                  <input value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Warranty Expiry</label>
                  <input type="date" value={form.warranty_expiry} onChange={e => setForm(f => ({ ...f, warranty_expiry: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Notes</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-[#3b4872] text-white text-sm py-2 rounded-sm hover:bg-[#2d3856] font-display font-bold" data-testid="asset-submit-btn">
                  {editId ? 'Update' : 'Create Asset'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 border border-slate-200 rounded-sm text-sm hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Audit modal */}
      {auditTarget && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-sm p-6 space-y-4">
            <h2 className="font-display font-bold text-lg">Record Physical Audit</h2>
            <form onSubmit={handleAudit} className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Audited By *</label>
                <input required value={auditForm.audited_by} onChange={e => setAuditForm(f => ({ ...f, audited_by: e.target.value }))}
                  className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid="audit-by-input" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Condition</label>
                <select value={auditForm.condition} onChange={e => setAuditForm(f => ({ ...f, condition: e.target.value }))}
                  className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm">
                  {['new', 'good', 'fair', 'poor', 'damaged'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="loc_confirmed" checked={auditForm.location_confirmed}
                  onChange={e => setAuditForm(f => ({ ...f, location_confirmed: e.target.checked }))} />
                <label htmlFor="loc_confirmed" className="text-sm text-slate-600">Location Confirmed</label>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Next Audit Due</label>
                <input type="date" value={auditForm.next_audit_due} onChange={e => setAuditForm(f => ({ ...f, next_audit_due: e.target.value }))}
                  className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Remarks</label>
                <textarea rows={2} value={auditForm.remarks} onChange={e => setAuditForm(f => ({ ...f, remarks: e.target.value }))}
                  className="w-full border border-slate-200 rounded-sm px-2.5 py-1.5 text-sm" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-[#3b4872] text-white text-sm py-2 rounded-sm hover:bg-[#2d3856] font-display font-bold" data-testid="audit-submit-btn">
                  Record Audit
                </button>
                <button type="button" onClick={() => setAuditTarget(null)} className="px-4 py-2 border border-slate-200 rounded-sm text-sm hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
